/**
 * @architecture-header 1
 * @layer module
 * @domain curriculum
 * @responsibility Create, update, archive, and reopen Organization Knowledge Components.
 * @state-owner TanStack Query mutation state
 * @allowed-imports app environment; TanStack Query; module query and model
 * @forbidden routes; components; direct generated SDK
 * @public-api via modules/curriculum/index.ts
 * @side-effects authenticated KC mutation requests outside demo mode
 * @naming-hook POST -> useCreate<Resource>; PATCH entity -> useUpdate<Resource>
 * @naming-path plural lowercase nouns; organization is the active-context scope hint
 * @naming-body camelCase; never caller role and never a server-side key
 * @tests curriculum mutation tests
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import type { CurriculumOrganizationKnowledgeComponent } from '../model/types';
import { knowledgeComponentSchema, updateKnowledgeComponentFixture } from './curriculum.queries';

const knowledgeComponentPath = '/api/v1/knowledge-components';

function demoKnowledgeComponent(input: {
  organizationId: string;
  name: string;
  standardCode?: string;
  description?: string;
}): CurriculumOrganizationKnowledgeComponent {
  const now = new Date().toISOString();
  return {
    id: globalThis.crypto?.randomUUID?.() ?? '00000000-0000-4000-8000-000000000098',
    organization_id: input.organizationId,
    name: input.name.trim(),
    description: input.description?.trim() ?? '',
    standard_code: input.standardCode?.trim() || null,
    revision: 1,
    archived: false,
    archived_at: null,
    created_at: now,
    updated_at: now,
  };
}

export function useCreateKnowledgeComponent() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      activeContextId: string;
      organizationId: string;
      name: string;
      standardCode?: string;
      description?: string;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const component = demoKnowledgeComponent(input);
        updateKnowledgeComponentFixture((components) => [...components, component]);
        return component;
      }
      return knowledgeComponentSchema.parse(
        await environment.apiClient.request<unknown>(knowledgeComponentPath, {
          method: 'POST',
          body: {
            organizationId: input.organizationId,
            name: input.name.trim(),
            ...(input.standardCode === undefined
              ? {}
              : { standardCode: input.standardCode.trim() }),
            ...(input.description === undefined ? {} : { description: input.description.trim() }),
            idempotencyKey: input.idempotencyKey,
          },
        }),
      );
    },
    onSuccess: (_component, input) =>
      queryClient.invalidateQueries({
        queryKey: ['knowledge-components', input.activeContextId, input.organizationId],
      }),
  });
}

export function useUpdateKnowledgeComponent() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      activeContextId: string;
      organizationId: string;
      component: CurriculumOrganizationKnowledgeComponent;
      name?: string;
      standardCode?: string | null;
      description?: string;
      archived?: boolean;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const updated = {
          ...input.component,
          ...(input.name === undefined ? {} : { name: input.name.trim() }),
          ...(input.standardCode === undefined
            ? {}
            : { standard_code: input.standardCode?.trim() || null }),
          ...(input.description === undefined ? {} : { description: input.description.trim() }),
          ...(input.archived === undefined
            ? {}
            : {
                archived: input.archived,
                archived_at: input.archived ? new Date().toISOString() : null,
              }),
          revision: input.component.revision + 1,
          updated_at: new Date().toISOString(),
        } satisfies CurriculumOrganizationKnowledgeComponent;
        updateKnowledgeComponentFixture((components) =>
          components.map((component) =>
            component.id === input.component.id ? updated : component,
          ),
        );
        return updated;
      }
      return knowledgeComponentSchema.parse(
        await environment.apiClient.request<unknown>(
          `${knowledgeComponentPath}/${encodeURIComponent(input.component.id)}`,
          {
            method: 'PATCH',
            body: {
              organizationId: input.organizationId,
              ...(input.name === undefined ? {} : { name: input.name.trim() }),
              ...(input.standardCode === undefined
                ? {}
                : { standardCode: input.standardCode?.trim() || null }),
              ...(input.description === undefined ? {} : { description: input.description.trim() }),
              ...(input.archived === undefined ? {} : { archived: input.archived }),
              expectedRevision: input.component.revision,
              idempotencyKey: input.idempotencyKey,
            },
          },
        ),
      );
    },
    onSuccess: (_component, input) =>
      queryClient.invalidateQueries({
        queryKey: ['knowledge-components', input.activeContextId, input.organizationId],
      }),
  });
}
